'use client';

import type { JSX } from 'react';

import ProjectionLabel, { ProjectionType } from 'DesignSystem2/Projection/ProjectionLabel';
import Palette from 'DesignSystem2/Theme/Palette';

import { useGraphPayload } from '../interaction/GraphInteractionContext';
import GraphHeaderShell from './GraphHeaderShell';
import type { NumericForecastPayload } from './payloads';

const renderProjection = (payload: NumericForecastPayload): JSX.Element | undefined => {
    if (payload.mode === 'hidden') return undefined;
    if (payload.mode === 'chance') {
        return (
            <div className="mb-1">
                <ProjectionLabel
                    type={ProjectionType.CHANCE}
                    projection={payload.projection}
                    change={payload.change}
                    color={payload.vibrant ? Palette.Brand.Primary : Palette.Text.x10}
                    customLabel={payload.customLabel}
                    dontAnimate={payload.isHovering}
                    size={payload.isCompact ? 'small' : 'large'}
                    hideTooltip={payload.isCompact || payload.vibrant}
                />
            </div>
        );
    }
    return (
        <div className="mb-1">
            <ProjectionLabel
                type={ProjectionType.FORECAST}
                projection={payload.projection}
                text={payload.text}
                change={payload.change}
                changeText={payload.changeText}
                color={payload.vibrant ? Palette.Brand.Primary : Palette.Text.x10}
                dontAnimate={payload.isHovering}
                size={payload.isCompact ? 'small' : 'large'}
                hideTooltip={payload.isCompact || payload.vibrant}
            />
        </div>
    );
};

/**
 * Header for the numeric event forecast chart. Builds either the CHANCE
 * (highlighted-market) or FORECAST `<ProjectionLabel>` from the body's
 * published `mode` discriminator.
 */
const NumericForecastHeader = () => {
    const payload = useGraphPayload<NumericForecastPayload>('numericForecast');
    if (!payload) return null;
    // Hide while genuinely unavailable OR still loading — `isLoading` keeps the
    // header collapsed during load (no flash of a `0` projection) now that
    // `chartUnavailable` no longer folds loading in (so the toggle-row logo can
    // stay visible through the load).
    if (payload.chartUnavailable || payload.isLoading) return <GraphHeaderShell />;
    return <GraphHeaderShell projection={renderProjection(payload)} />;
};

export default NumericForecastHeader;
